import { useState } from 'react'
import { useTranslation } from '@/i18n/hooks/useTranslation'
import { useSimpleNotifications } from '@/features/notifications/SimpleNotificationProvider'
import { useChallenges } from '@/features/challenges/ChallengeProvider'
// import { rescheduleAllNotifications } from '@/utils/simpleChallengeUtils'
// import { useQuery } from '@tanstack/react-query'

export function SimpleNotificationSettings() {
  const { t } = useTranslation()
  const { isSupported, permission, requestPermission, getStatus } = useSimpleNotifications()
  const { challenges } = useChallenges() // Use challenges from context instead
  const [isRescheduling, setIsRescheduling] = useState(false)

  // const { data: challenges = [] } = useQuery({
  //   queryKey: ['challenges'],
  //   queryFn: () => challengeRepository.getChallenges('en'), // Get active challenges
  // })

  const status = getStatus()

  const handleEnableNotifications = async () => {
    try {
      await requestPermission()
    } catch (error) {
      console.error('Failed to request notification permission:', error)
    }
  }

  const handleRescheduleNotifications = async () => {
    setIsRescheduling(true)
    try {
      // TODO: Implement notification rescheduling for challenges
      // await rescheduleAllNotifications(challenges)
      console.log('Notification rescheduling not implemented yet')
    } catch (error) {
      console.error('Failed to reschedule notifications:', error)
    } finally {
      setIsRescheduling(false)
    }
  }

  if (!isSupported) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-full p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">
          {t('notifications.notSupported', 'Notifications Not Supported')}
        </h3>
        <p className="text-sm text-yellow-700">
          {t('notifications.notSupportedDescription', 'Your browser does not support notifications.')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-full p-4">
        <h3 className="font-semibold text-gray-900 mb-4">
          {t('notifications.settings', 'Notification Settings')}
        </h3>
        
        <div className="space-y-3">
          {/* Permission Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {t('notifications.permission', 'Permission Status')}
            </span>
            <span className={`text-sm font-medium ${
              permission === 'granted' ? 'text-green-600' : 
              permission === 'denied' ? 'text-red-600' : 'text-yellow-600'
            }`}>
              {permission === 'granted' ? t('notifications.granted', 'Granted') :
               permission === 'denied' ? t('notifications.denied', 'Denied') :
               t('notifications.default', 'Not Asked')}
            </span>
          </div>

          {/* Scheduled Notifications */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {t('notifications.scheduled', 'Scheduled Notifications')}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {status.scheduledCount}
            </span>
          </div>

          {/* Platform */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {t('notifications.platform', 'Platform')}
            </span>
            <span className="text-sm font-medium text-gray-900">
              {status.platform}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 space-y-2">
          {permission !== 'granted' && (
            <button
              onClick={handleEnableNotifications}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              {t('notifications.enable', 'Enable Notifications')}
            </button>
          )}

          {permission === 'granted' && challenges.length > 0 && (
            <button
              onClick={handleRescheduleNotifications}
              disabled={isRescheduling}
              className="w-full bg-gray-600 text-white px-4 py-2 rounded-full hover:bg-gray-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRescheduling 
                ? t('notifications.rescheduling', 'Rescheduling...') 
                : t('notifications.reschedule', 'Reschedule All Notifications')}
            </button>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-full p-4">
        <h4 className="font-medium text-blue-900 mb-2">
          {t('notifications.helpTitle', 'How it works')}
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• {t('notifications.help1', 'Notifications are sent locally by your browser')}</li>
          <li>• {t('notifications.help2', 'They work even when the app is closed')}</li>
          <li>• {t('notifications.help3', 'Click on a notification to open the app')}</li>
          <li>• {t('notifications.help4', 'Notifications are automatically scheduled for your active challenges')}</li>
        </ul>
      </div>
    </div>
  )
} 